import cn from "classnames";
import { message as antdMessage } from "antd";
import React, { ReactNode, CSSProperties, MouseEvent } from "react";
import type { ArgsProps, MessageType } from "antd/lib/message";

import styles from "./Message.module.css";

type NoticeType = "success" | "error" | "info" | "warning" | "loading";

export interface MessageProps extends Omit<ArgsProps, "type" | "content"> {
  /** Message body — string or any React node. */
  content: ReactNode;
  /** Font size override applied to the message content. Accepts any valid CSS size. */
  fontSize?: string | number;
  /**
   * When true (default) the toast is given a pointer cursor and a click on the
   * content will dismiss it immediately. Set to false for non-interactive toasts.
   */
  dismissible?: boolean;
  /**
   * Called when the toast is clicked. Declared explicitly because some antd v4
   * minor versions ship `ArgsProps` without an `onClick` field.
   */
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

/**
 * Either a full props object (matches antd `ArgsProps` plus our extensions)
 * or just a content node — same flexibility as antd's `message.success`.
 */
export type MessageInput = MessageProps | ReactNode;

const isProps = (input: MessageInput): input is MessageProps =>
  typeof input === "object" &&
  input !== null &&
  !React.isValidElement(input) &&
  Object.prototype.hasOwnProperty.call(input as Record<string, unknown>, "content");

const buildArgs = (input: MessageInput) => {
  const props: MessageProps = isProps(input)
    ? input
    : ({ content: input as ReactNode } as MessageProps);
  const { content, fontSize, dismissible = true, style, className, onClick, ...rest } = props;

  // Captured by closure so the inner click handler can dismiss the exact
  // toast that antd hands back from message.success/error/etc.
  const closeRef: { current?: MessageType } = {};
  const close = () => closeRef.current?.();

  const mergedStyle: CSSProperties = {
    ...(fontSize !== undefined ? { fontSize } : null),
    ...(dismissible ? { cursor: "pointer" } : null),
    ...style,
  };

  const handleContentClick = (e: MouseEvent<HTMLSpanElement>) => {
    onClick?.((e as unknown) as MouseEvent<HTMLDivElement>);
    close();
  };

  // antd v4 sets `pointer-events: none` on `.ant-message` and only re-enables
  // it on `.ant-message-notice-content`. Wrapping the body in a span with
  // pointer-events: auto guarantees the click lands.
  const renderedContent: ReactNode = dismissible ? (
    <span
      className={styles.content}
      data-testid="ams-message-content"
      role="button"
      tabIndex={0}
      onClick={handleContentClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          close();
        }
      }}
    >
      {content}
    </span>
  ) : (
    content
  );

  const config = {
    ...rest,
    className: cn(styles.message, className),
    content: renderedContent,
    onClick: dismissible
      ? (e: MouseEvent<HTMLDivElement>) => {
          onClick?.(e);
          close();
        }
      : onClick,
    style: mergedStyle,
  } as Omit<ArgsProps, "type">;

  return {
    config,
    setHide: (h: MessageType) => {
      closeRef.current = h;
    },
  };
};

const open = (type: NoticeType, input: MessageInput): MessageType => {
  const { config, setHide } = buildArgs(input);
  const fn = antdMessage[type] as (args: Omit<ArgsProps, "type">) => MessageType;
  const hide = fn(config);
  setHide(hide);
  return hide;
};

/**
 * Drop-in replacement for antd's `message` API.
 *
 * Differences from antd:
 *  - Toasts are click-to-dismiss by default (cursor pointer + keyboard support).
 *  - Accepts a `fontSize` shortcut that maps to inline style.
 *  - Pass `dismissible: false` to opt out of click-to-dismiss.
 *
 * Usage:
 *   Message.success({ content: "Saved", duration: 5 });
 *   Message.error("Something went wrong");
 *   const hide = Message.info({ content: "…", dismissible: false });
 *   hide();
 */
export const Message = {
  /** Pass-through to antd's global message config (top, duration, etc.). */
  config: antdMessage.config.bind(antdMessage),
  /** Dismiss every active toast. */
  destroy: (): void => antdMessage.destroy(),
  error: (input: MessageInput): MessageType => open("error", input),
  info: (input: MessageInput): MessageType => open("info", input),
  loading: (input: MessageInput): MessageType => open("loading", input),
  success: (input: MessageInput): MessageType => open("success", input),
  warning: (input: MessageInput): MessageType => open("warning", input),
};
